"use client";

import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import { useState, useEffect, useCallback } from "react";

// Clean color palette - Only Deep Teal Green and White
const colors = {
  primary: "#395750",      // Deep Teal Green - Company color
  primaryDark: "#2A423C",  // Darker teal
  primaryLight: "#4B6B63", // Lighter teal
  white: "#FFFFFF",        // White
  whiteOff: "#F8F9FA",     // Off-white for subtle contrast
  textDark: "#1A2E29",     // Dark text for contrast
};

// Single-size hero banners (all 4134x1379) - slide in this sequence
const slides = [
  // "/images/about/newyear.webp",
  "/images/about/7.webp",
  "/images/about/8.webp",
  "/images/about/ifmb34.webp",
  "/images/about/6188.webp",


  
];

const variants = {
  enter: (direction: number) => ({
    x: direction > 0 ? "100%" : "-100%",
    opacity: 0,
    scale: 0.92,
  }),
  center: {
    x: 0,
    opacity: 1,
    scale: 1,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? "-100%" : "100%",
    opacity: 0,
    scale: 0.96,
  }),
};

export const HeroSectionhome = () => {
  const [[current, direction], setCurrentState] = useState([0, 0]);
  const [paused, setPaused] = useState(false);

  const goToNext = useCallback(() => {
    setCurrentState(([current]) => [(current + 1) % slides.length, 1]);
  }, []);

  const goToPrev = useCallback(() => {
    setCurrentState(([current]) => [(current - 1 + slides.length) % slides.length, -1]);
  }, []);

  const goToSlide = useCallback((index: number) => {
    setCurrentState(([current]) => [index, index > current ? 1 : -1]);
  }, []);

  useEffect(() => {
    if (paused) return;
    const interval = setInterval(goToNext, 7000);
    return () => clearInterval(interval);
  }, [goToNext, paused]);

  return (
    <section className="relative mt-[92px] w-full overflow-hidden bg-transparent xl:mt-[108px]">
      {/* Banner - height follows the image's aspect ratio so nothing is cropped */}
      <div
        className="relative aspect-[4134/1379] w-full"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {/* Auto-sliding Banner Slides */}
        <AnimatePresence initial={false} custom={direction}>
          <motion.div
            key={current}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0"
          >
            <Image
              src={slides[current]}
              alt={`Yegna banner ${current + 1}`}
              fill
              priority
              quality={100}
              sizes="100vw"
              className="object-cover"
            />
            {/* Light sheen sweep across the banner as it enters */}
            <motion.div
              className="pointer-events-none absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/15 to-transparent"
              initial={{ x: "-150%" }}
              animate={{ x: "400%" }}
              transition={{ duration: 1.1, delay: 0.2, ease: "easeInOut" }}
            />
          </motion.div>
        </AnimatePresence>

        {/* YEGNA FOR ALL! Badge - Fixed to left bottom corner */}
        <div className="absolute bottom-4 left-4 z-30 sm:bottom-6 sm:left-6">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            whileHover={{ scale: 1.05 }}
            className="group relative inline-block"
          >
            <div
              className="rounded-lg px-3 py-1.5 text-xs font-bold tracking-wide text-white shadow-md transition-all duration-300 sm:px-5 sm:py-2.5 sm:text-base"
              style={{
                backgroundColor: colors.primary,
                boxShadow: `0 4px 15px ${colors.primary}30`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = colors.white;
                e.currentTarget.style.color = colors.primary;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = colors.primary;
                e.currentTarget.style.color = colors.white;
              }}
            >
              YEGNA FOR ALL!
              <motion.span
                className="absolute inset-0 rounded-lg border-2 opacity-0 group-hover:opacity-100"
                style={{ borderColor: colors.primary }}
                initial={{ opacity: 0 }}
                whileHover={{
                  opacity: 1,
                  borderColor: [colors.primary, `${colors.white}CC`, colors.primary],
                }}
                transition={{ duration: 1.5, repeat: Infinity, repeatType: "reverse" }}
              />
            </div>
            <motion.div
              className="absolute -inset-1 -z-10 rounded-lg transition-all duration-500"
              style={{ backgroundColor: `${colors.primary}20` }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            />
          </motion.div>
        </div>

        {/* Navigation Arrows */}
        <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-between px-2 sm:px-4">
          <button
            onClick={goToPrev}
            className="pointer-events-auto rounded-full bg-black/20 p-1.5 text-white backdrop-blur-sm transition-all duration-300 hover:bg-white/30 sm:p-2 md:p-3"
            aria-label="Previous slide"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6 md:h-8 md:w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={goToNext}
            className="pointer-events-auto rounded-full bg-black/20 p-1.5 text-white backdrop-blur-sm transition-all duration-300 hover:bg-white/30 sm:p-2 md:p-3"
            aria-label="Next slide"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6 md:h-8 md:w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Indicators */}
        <div className="absolute bottom-3 left-0 right-0 z-20 flex justify-center gap-1.5 sm:bottom-6 sm:gap-2">
          {slides.map((slide, index) => (
            <motion.button
              key={slide}
              onClick={() => goToSlide(index)}
              className="h-1.5 rounded-full transition-all duration-300 sm:h-2"
              style={{
                backgroundColor: current === index ? colors.white : `${colors.white}80`,
              }}
              aria-label={`Go to slide ${index + 1}`}
              whileHover={{ scale: 1.3 }}
              animate={{
                width: current === index ? 16 : 6,
              }}
              transition={{ duration: 0.3 }}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
